import { Effect } from 'effect';

import type { PageRequest } from '@vs-shop/domain/pagination';
import { ProductCatalogue } from '@vs-shop/engine/capability';
import { capabilityLayer } from '@vs-shop/engine/capability/capabilityLayer';
import { ProductCatalogueError } from '@vs-shop/engine/capability/productCatalogue/errors';
import { createBackendApiError } from '@vs-shop/engine/data/error/backendApi/_internal/createBackendApiError';
import { makeApiRequest } from '@vs-shop/engine/service/api/makeApiRequest';
import { ApiModule } from '@vs-shop/engine/service/logger/ApiModule';
import { HydraCollectionResponse } from '@vs-shop/schemas/openapi/response';

import { ProductBase } from '../schema/openapi/backend';
import { BackendApi } from '../service/api/BackendApi';

import { toProductPage } from './productListing';

/**
 * rcprofi's answer to {@link ProductCatalogue}: the catalogue, one page at a
 * time, in the backend's own order.
 *
 * The same endpoint `CategoryListing.products` reads, and a separate operation.
 * The two differ in one query parameter and that parameter is the whole point:
 * this read names no taxon, so it answers the shop rather than a category. It
 * carries its own span and its own error family for the same reason.
 *
 * This is the one rcprofi capability with no bind export behind it, because
 * rcprofi's request tier has no unfiltered products read to bind — every
 * products call the storefront makes today names a taxon or a brand context.
 * The read is therefore one call on the engine's request template, which is
 * what supplies the client span, the `ValidateResponse` span, the operation
 * span and the log line.
 *
 * `inStock: true` is rcprofi's, exactly as it is in `getProductsByTaxon`. It
 * says what this shop's catalogue is and not what a catalogue means, so it
 * stays where the request is built and never reaches the port.
 */

const ENDPOINT = '/api/v2/shop/products';

const ListProductsError = createBackendApiError({
  domain: 'Product',
  endpoint: 'List',
  path: ENDPOINT,
  identifierField: 'page',
});

const listProducts = makeApiRequest(BackendApi)({
  path: ENDPOINT,
  method: 'get',
  accept: 'application/ld+json',
  schema: HydraCollectionResponse(ProductBase),
  errors: ListProductsError,
  module: ApiModule.ProductList,
});

const products = (asked: PageRequest) =>
  listProducts(
    { query: { page: asked.page, itemsPerPage: asked.itemsPerPage, inStock: true } },
    asked.page
  ).pipe(
    Effect.map((listing) => toProductPage(listing, asked)),
    Effect.mapError(ProductCatalogueError.fromRequestFailure('products', String(asked.page)))
  );

export const ProductCatalogueLive = capabilityLayer(ProductCatalogue, {
  products,
});
